import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { fetchRoundPicks } from './api';
import { feedAvatarUrl } from './RoundFeed';
import type { MilharTierId } from '../../shared/milhar/tiers';
import type { FeedOrder, RoundFeed } from '../../shared/milhar/roundFeedView';

/** 号码被这么多人选中即转金色。定稿 §1.2 —— 这是集中度对冲唯一的视觉强调。 */
const HOT_PICKERS = 20;

/**
 * 本期买家名单。定稿 `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §1.1 / §1.2。
 *
 * <p>这个 sheet 是 §12C「绝不公布是谁」被推翻后的产物 —— 它**故意**公开身份、
 * 金额与号码。三条不可动的配套：
 *
 * <ol>
 * <li>顶部免责句复用 `milharRoundNumbersHint`，与 §12C 的分布 sheet **同一个
 *     i18n key**。共用一个 key 才能保证两处文案物理上不可能漂移。
 * <li>该句**不可折叠、不可变灰、必须第一屏** —— 公开号码会加剧
 *     `MASTER_PRD §569` 说的集中度风险，这句加上每行的 co-picker 数是唯一的
 *     反向杠杆。
 * <li>头像点击**就地开公开资料**，不跳排行榜页 —— 跳页会丢光选号状态（§3.2）。
 *     `PublicProfileSheet` 已从 `LeaderboardPage` 抽到 `features/profile/`，两边共用同
 *     一个组件。<b>不要退回「跳排行榜页再跳回」的接法</b> —— 那正是 §3.2 要避免的。
 * </ol>
 */
export function RoundBuyersSheet({
  feed,
  currency,
  roundNumber,
  myUserId,
  token,
  tier = '4',
  onOpenProfile,
  onClose,
}: {
  feed: RoundFeed;
  currency: string;
  roundNumber: number;
  myUserId?: number | null;
  token?: string | null;
  tier?: MilharTierId;
  onOpenProfile?: (userId: number, username: string) => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  // co-picker 数复用 §12C 已有的分布接口，不新开一个 —— 同一份事实两个来源
  // 迟早会对不上，而这里对不上的后果是「跟买 = 分钱」这条提示失真。
  const picks = useQuery({
    queryKey: ['milhar-round-picks', tier],
    queryFn: () => fetchRoundPicks(token, tier),
    staleTime: 10_000,
  });
  const peopleByPick = new Map<string, number>(
    (picks.data?.picks || []).map((row) => [row.pick, row.people]),
  );

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="profile-sheet milhar-buyers-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('milharBuyersTitle')}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div className="flex-1">
            <h2>{t('milharBuyersTitle')}</h2>
            <p className="milhar-buyers-sub">
              {t('milharBuyersCount')
                .replace('%p%', String(feed.people))
                .replace('%t%', String(feed.tickets))
                .replace('%r%', String(roundNumber))}
            </p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {/* 不可折叠、不可变灰 —— 见文件头注释第 2 条 */}
        <p className="milhar-picks-disclaimer">{t('milharRoundNumbersHint')}</p>

        <ul className="milhar-buyers-list">
          {feed.orders.map((order) => (
            <BuyerRow
              key={order.id}
              order={order}
              currency={currency}
              people={peopleByPick.get(order.pick) ?? 0}
              mine={!!myUserId && order.userId === myUserId}
              onOpenProfile={onOpenProfile}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function BuyerRow({
  order,
  currency,
  people,
  mine,
  onOpenProfile,
}: {
  order: FeedOrder;
  currency: string;
  people: number;
  mine: boolean;
  onOpenProfile?: (userId: number, username: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const url = feedAvatarUrl(order);
  const className = `milhar-buyers-row${onOpenProfile ? ' tap' : ' is-static'}${mine ? ' is-mine' : ''}`;
  // 打开时滚到自己那行，沿用 RoundPicksSheet 的既有做法
  const anchor = mine
    ? (el: HTMLElement | null) => el?.scrollIntoView({ block: 'center' })
    : undefined;
  const inner = (
    <>
        {url ? (
          <img className="milhar-feed-av" src={url} alt="" width={34} height={34} loading="lazy" />
        ) : (
          <span className="milhar-feed-av is-letter" style={{ width: 34, height: 34 }}>
            {order.username.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <span className="milhar-buyers-name">{mine ? t('milharYourNumber') : order.username}</span>
        <span className="milhar-buyers-amt mono">{fmtCurrency(Number(order.amount), currency)}</span>
        <span className="milhar-buyers-pick mono">{order.pick}</span>
      {/* 这一格**永远渲染**，拿不到数就留空。
          分布接口有 DISTRIBUTION_LIMIT=100 的上限（按人数降序），冷门号会掉出来；
          原来那版拿不到就整个不渲染，于是相邻行的号码列左右串位，看着像没对齐。
          按人数降序意味着掉出来的必然是不拥挤的号 —— 对冲要强调的那些永远在榜内。 */}
      <span className={`milhar-buyers-co${people >= HOT_PICKERS ? ' is-hot' : ''}`}>
        {people > 0 ? `·${people}` : ''}
      </span>
    </>
  );
  return (
    <li>
      {onOpenProfile ? (
        <button type="button" className={className} ref={anchor}
          onClick={() => onOpenProfile(order.userId, order.username)}>
          {inner}
        </button>
      ) : (
        <div className={className} ref={anchor}>{inner}</div>
      )}
    </li>
  );
}
